import React, { useState } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { IoSearchSharp } from "react-icons/io5";
import TableMui from "../../components/mui/TableMui";
import { IconButton, Menu, MenuItem } from "@mui/material";
import Pagination from "../../components/Pagination";
import {
  useDeleteReviewOnReviewIdMutation,
  useGetAllReviewsAdminQuery,
} from "../../redux/clientApi";
import LoadingScreen from "../LoadingScreen";
import { getCorrectDate } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DeletionDialog from "../DeletionDialog";

const AdminReviews = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const { t } = useTranslation();

  const { data, isLoading } = useGetAllReviewsAdminQuery({
    page: currentPage,
    search: search,
  });

  const pagination = data?.data?.pagination || {};
  const reviews = data?.data?.reviews || [];

  const [deleteReview, { isLoading: isDeleting }] =
    useDeleteReviewOnReviewIdMutation();

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const [deletionReviewId, setDeletionReviewId] = useState(null);

  const headers = {
    content: t("adminReviews.tableHeaderReview"),
    reviewBy: t("adminReviews.tableHeaderUser"),
    "placeId.organizationName": t("adminReviews.tableHeaderPlace"),
    rating: t("adminReviews.tableHeaderRating"),
    createdAt: t("adminReviews.tableHeaderCreatedAt"),
    actions: t("adminReviews.tableHeaderActions"),
  };

  const dialogOpen = () => {
    setIsDeletionDialogOpen(true);
  };

  const cancelDelete = () => {
    setIsDeletionDialogOpen(false);
    setDeletionReviewId(null);
  };

  const confirmDelete = async () => {
    if (!deletionReviewId) return;

    try {
      // Using .unwrap() for better error handling with RTK Query
      const res = await deleteReview(deletionReviewId);

      if (
        res?.error?.data?.status === "error" ||
        res?.error?.data?.status === "fail"
      ) {
        throw new Error(
          res?.error?.data?.message || t("adminReviews.toastDeleteError"),
        );
      }

      // Prefer backend message if available, otherwise fallback to translation
      const successMessage =
        res?.data?.message || t("adminReviews.toastDeleteSuccess");

      toast.success(successMessage);
      cancelDelete();
    } catch (error) {
      // Prefer backend error message if available, otherwise fallback
      const errorMessage = error?.message || t("adminReviews.toastDeleteError");

      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="mt-[60px] md:mt-[100px] max-w-[1300px] mx-auto pt-4 pb-12 px-6 sm:px-12 bg-white">
      {/* Heading + Search bar */}
      <div className="flex flex-col md:flex-row gap-3 justify-between items-center">
        <div>
          <h2 className="text-[20px] sm:text-[24px] md:text-[30px] lg:text-[40px] font-semibold mt-8 md:mt-0">
            {t("adminReviews.reviewsManagement")}
          </h2>
          <p className="max-w-[600px] text-[14px] sm:text-[16px]">
            {t("adminReviews.manageReviewsDescription")}
          </p>
        </div>

        <div className="flex items-center w-full sm:w-[calc(30%-30px)] p-1 h-10 bg-[#F7F7F7] border border-[#EECB3D] rounded-md">
          <div className="p-2 bg-[#EECB3D] rounded-md">
            <IoSearchSharp />
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder={t("adminReviews.searchPlaceholder")}
            className="ml-2 flex-1 p-1 outline-none bg-transparent text-sm"
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="mt-12">
        <TableMui
          th={headers}
          td={reviews}
          customFields={[
            {
              name: "reviewBy",
              data: (value) => (
                <span className="capitalize font-medium">
                  {`${value?.firstName || ""} ${value?.lastName || ""}`}
                </span>
              ),
            },
            {
              name: "rating",
              data: (value) => <span className="font-medium">{value} ★</span>,
            },
            {
              name: "content",
              data: (value) => <div className="max-w-xs truncate">{value}</div>,
            },
            {
              name: "createdAt",
              data: (value) => (
                <span className="capitalize font-medium">
                  {getCorrectDate(t, value)}
                </span>
              ),
            },
            {
              name: "actions",
              data: (_, row) => (
                <ActionMenu
                  row={row}
                  dialogOpen={dialogOpen}
                  setDeletionReviewId={setDeletionReviewId}
                />
              ),
            },
          ]}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages || 0}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Reusable Deletion Dialog */}
      <DeletionDialog
        open={isDeletionDialogOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title="admin-places.dialog.title"
        message="admin-places.dialog.message"
        confirmText="admin-places.dialog.confirm"
        cancelText="admin-places.dialog.cancel"
      />
    </div>
  );
};

const ActionMenu = ({ row, dialogOpen, setDeletionReviewId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeletionReviewId(row?._id);
    dialogOpen();
    handleClose();
  };

  return (
    <div>
      <IconButton onClick={handleClick}>
        <PiDotsThreeOutlineVerticalFill />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "red" }}>
          {t("adminReviews.actionDelete")}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default AdminReviews;
